// Look here for custom material implementation:
// https://codesandbox.io/s/phuwd?file=/src/material.js
import * as THREE from "three";
import React, { useState } from "react";
import { MeshPhongMaterial } from "three";

class InstancedPhongImpl extends MeshPhongMaterial {
  _animationDelta;
  _animationStart;
  _clock;
  _isPulsating;
  _isBreathing;
  _tOpacity;
  constructor(parameters = {}) {
    super(parameters);
    this.setValues(parameters);
    this._animationDelta = { type: "f", value: null };
    this._animationStart = { type: "f", value: null };
    this._animationStop = { type: "f", value: null };
    this._clock = { type: "f", value: 1.0 };
    // this animates opacity [0, 1]
    this._isPulsating = { value: false };
    // this animates model matrix [0, 1]
    this._isBreathing = { value: false };
  }

  onBeforeCompile(shader) {
    shader.uniforms.animationDelta = this._animationDelta;
    shader.uniforms.clock = this._clock;
    // global itme
    shader.uniforms.animationStart = this._animationStart;
    shader.uniforms.animationStop = this._animationStop;
    shader.uniforms.isPulsating = this._isPulsating;
    shader.uniforms.isBreathing = this._isBreathing;
    shader.vertexShader = `
      uniform float animationDelta;
      uniform float animationStart;
      uniform float animationStop;
      uniform float clock;
      uniform bool isBreathing;
      attribute float animationDelay;
      attribute float instanceId;
      attribute float instanceOpacity;
      attribute float tOpacity;

      varying float vId;
      varying float vAnimationProgress;
      // varying float vAnimationDelay;
      varying float vSrcOpacity;
      varying float vTargetOpacity;
      
      #ifdef USE_DATE_FUZZINESS
        attribute float renderFuzziness;
        varying float vRenderFuzziness;
      #endif

      #ifdef USE_MAKE_HOLE
        attribute float makeHole;
        varying float vMakeHole;
      #endif

      #ifdef USE_FRESNEL_SHADING
        varying vec3 vNN;
        varying vec3 vEye;
      #endif

      float map(float value, float min1, float max1, float min2, float max2) {
        return min2 + (value - min1) * (max2 - min2) / (max1 - min1);
      }

      // float rand(float n){return fract(sin(n) * 43758.5453123);}

      // float noise(float p){
      //   float fl = floor(p);
      //   float fc = fract(p);
      //   return mix(rand(fl), rand(fl + 1.0), fc);
      // }

      //  Function from Iñigo Quiles
      //  www.iquilezles.org/www/articles/functions/functions.htm
      float parabola( float x, float k ){
          return pow( 4.0*x*(1.0-x), k );
      }

      //  Function from Iñigo Quiles
      //  www.iquilezles.org/www/articles/functions/functions.htm
      float cubicPulse( float c, float w, float x ){
          x = abs(x - c);
          if( x>w ) return 0.0;
          x /= w;
          return 1.0 - x*x*(3.0-2.0*x);
      }

      float PHI = 1.61803398874989484820459;  // Φ = Golden Ratio

      ${shader.vertexShader}
    `;
    shader.vertexShader = shader.vertexShader.replace(
      "#include <project_vertex>",
      `
      // global timestamps, passed to fragment shader
      float startTime = animationStart + animationDelay;
      float endTime = animationStop + animationDelay;
      float animationProgress = map(
        clamp(clock, startTime, endTime),
        startTime, endTime,
        0., 1.
      );
      vAnimationProgress = animationProgress;

      // perform scaling below
      // but only for new ones entering
      if (instanceOpacity == .0) {
        // transformed *= 1. + parabola(animationProgress, 1.0);
        transformed *= 1. + cubicPulse(0.3, 0.3, animationProgress);
      }

      // make them breathe
      if (isBreathing) {
        transformed *= 1. + sin(clock*2. + instanceId) * 0.2;
      }

      #ifdef USE_DATE_FUZZINESS
        vRenderFuzziness = renderFuzziness;
      #endif

      #ifdef USE_MAKE_HOLE
        vMakeHole = makeHole;
      #endif

      #ifdef USE_FRESNEL_SHADING
        mat4 LM = modelMatrix;
        LM[2][3] = 0.0;
        LM[3][0] = 0.0;
        LM[3][1] = 0.0;
        LM[3][2] = 0.0;

        vec4 GN = LM * vec4(objectNormal.xyz, 1.0);
        // vec4 GN = LM * vec4(transformedNormal.xyz, 1.0);
        vNN = normalize(GN.xyz);
        vEye = normalize(GN.xyz-cameraPosition);
      #endif

       vec4 mvPosition = vec4( transformed, 1.0 );
      #ifdef USE_INSTANCING
        mvPosition = instanceMatrix * mvPosition;
      #endif
        mvPosition = modelViewMatrix * mvPosition;
        gl_Position = projectionMatrix * mvPosition;
        // vAnimationDelay = animationDelay;
        vSrcOpacity = instanceOpacity;
        vTargetOpacity = tOpacity;
        vId = float(instanceId);
        // gl_Position *= 4.;
        // gl_Position.x += noise(uv.x + vId + clock) * 1. - 2.;
        // gl_Position.y += noise(uv.y + vId + clock) * 1. - 2.;
        // gl_Position.z += noise(uv.y + vId + clock) * 1. - 2.;
        // gl_Position.y += noise(gl_Position.x) * 10. - 20.;
        // gl_Position.y -= mix(-100., 0., animationDelta);
        `
    );
    shader.fragmentShader = `
      varying float vId;
      varying float vAnimationProgress;
      // varying float vAnimationDelay;
      varying float vSrcOpacity;
      varying float vTargetOpacity;
      #ifdef USE_DATE_FUZZINESS
        varying float vRenderFuzziness; // this acts as a bool essentially
      #endif
      #ifdef USE_MAKE_HOLE
        varying float vMakeHole; // this acts as a bool essentially
      #endif
      #ifdef USE_FRESNEL_SHADING
        varying vec3 vNN;
        varying vec3 vEye;
      #endif
      uniform bool isPulsating;
      uniform float animationDelta;
      // keep track of global clock timestamp of last animation trigger
      uniform float animationStart;
      uniform float animationStop;
      uniform float clock;

      //  Function from Iñigo Quiles
      //  www.iquilezles.org/www/articles/functions/functions.htm
      float cubicPulse( float c, float w, float x ){
          x = abs(x - c);
          if( x>w ) return 0.0;
          x /= w;
          return 1.0 - x*x*(3.0-2.0*x);
      }

      float map(float value, float min1, float max1, float min2, float max2) {
        return min2 + (value - min1) * (max2 - min2) / (max1 - min1);
      }

      ${shader.fragmentShader}
    `;
    shader.fragmentShader = shader.fragmentShader.replace(
      `vec4 diffuseColor = vec4( diffuse, opacity );`,
      `
      // if (vSrcOpacity == 0.0 && vTargetOpacity == 0.0) discard;

      vec4 diffuseColor = vec4( diffuse, mix(vSrcOpacity, vTargetOpacity, clamp(vAnimationProgress, 0., 1.) ));

      #ifdef USE_DATE_FUZZINESS
        if (vRenderFuzziness > 0.5) {
          diffuseColor.w *= clamp(cubicPulse(0.5, 0.5, vUv.x), 0.01, 1.);
        }
      #endif

      if (isPulsating) {
        float t = abs(sin(clock + vId));
        diffuseColor.w *= clamp(t, .1, diffuseColor.w);
        // diffuseColor.w = 1./distance(gl_FragCoord.xy, vec2(0.5, .5));
        // diffuseColor.w *= 1./distance(gl_FragCoord.xy, vec2(.5, .5));
        // diffuseColor.w *= abs(sin(clock * 2. + vId));
      }

      #ifdef USE_MAKE_HOLE
        if (vMakeHole > 0.5) {
          // TODO This can look better
          diffuseColor.a *= map(distance(.5, vUv.y), 0., 0.5, 0.1, 1.);
        }
      #endif
      `
    );
    // shader.fragmentShader = shader.fragmentShader.replace(
    //   `gl_FragColor = vec4( outgoingLight, diffuseColor.a );`,
    //   `
    //   gl_FragColor = vec4( outgoingLight, diffuseColor.a );
    //   #ifdef USE_FRESNEL_SHADING
    //     #ifdef USE_MAKE_HOLE
    //       if (vMakeHole > 0.5) {
    //         float bias = 0.0;
    //         float scale = 1.;
    //         float power = 1.;
    //         float r = clamp(bias + scale * pow(1.0 + dot(vEye, normalize(vNN)), power), 0.0, 1.0);
    //         // gl_FragColor.rgb = mix(vec3(0.0, 0.0, 0.0), gl_FragColor.rgb, r);
    //         // gl_FragColor.a = mix(0.0, gl_FragColor.a, r);
    //         // gl_FragColor.a = mix(gl_FragColor.a, 0.0, 1.0 - r);
    //         // gl_FragColor.rgb +=  1.0 - -min(dot(vEye, normalize(vNN) ), 0.0);
    //       }
    //     #endif
    //   #endif
    //   `
    // );
  }

  get isPulsating() {
    return this._isPulsating.value;
  }
  set isPulsating(v) {
    this._isPulsating.value = v;
  }

  get isBreathing() {
    return this._isBreathing.value;
  }
  set isBreathing(v) {
    this._isBreathing.value = v;
  }

  get clock() {
    return this._clock.value;
  }
  set clock(v) {
    this._clock.value = v;
  }

  get animationStart() {
    return this._animationStart.value;
  }
  set animationStart(v) {
    this._animationStart.value = v;
  }

  get animationStop() {
    return this._animationStop.value;
  }
  set animationStop(v) {
    this._animationStop.value = v;
  }

  get animationDelta() {
    return this._animationDelta.value;
  }
  set animationDelta(v) {
    this._animationDelta.value = v;
  }
}

export const InstancedPhongMaterial = React.forwardRef((props, ref) => {
  const [material] = useState(() => new InstancedPhongImpl());
  return (
    <primitive
      object={material}
      ref={ref}
      side={THREE.DoubleSide}
      attach="material"
      {...props}
    />
  );
});

// export const AnimatedInstancedPhongMaterialAttributes = ({
//   colorArray = [],
//   opacityArray = [],
// }) => (
//   <>
//     <instancedBufferAttribute
//       attachObject={["attributes", "color"]}
//       args={[colorArray, 3]}
//     />
//     <instancedBufferAttribute
//       attachObject={["attributes", "instanceOpacity"]}
//       args={[opacityArray, 1]}
//     />
//   </>
// );
